import { create } from 'zustand';
import {
  AGREE_TO_CONTRACT,
  GET_CONTRACT,
  GET_CONTRACTS_BY_OWNER_ID,
  VERIFY_CONTRACT_STATUS,
} from './contract.gql';
import { getApolloClient } from '../lib/getApolloClient';
import { Contract } from '../generated/gql-types';
import { useCoreLoopConfigurationStore } from '../coreLoopConfiguration/useCoreLoopConfigurationStore';
import { pickContractStatusForOnboarding } from './contractOnboarding.util';

interface ContractStore {
  contract: Contract | null;
  setContract: (contract: Contract | null) => void;
  fetchContract: (pageContractType?: string | null) => Promise<void>;
  fetchContractById: (contractId: string) => Promise<Contract>;
  fetchContractsByOwnerId: (
    ownerId: string,
    tenantId: string,
    activeOnly?: boolean,
  ) => Promise<Contract[]>;
  agreeToContract: (
    contractId: string,
    signature?: string,
  ) => Promise<Contract>;
}

let fetchContractAbort: AbortController | null = null;

export const useContractStore = create<ContractStore>((set) => ({
  //   state
  contract: null,

  //   actions
  setContract: (contract: Contract | null) => set({ contract }),

  //   mutations
  fetchContract: async (pageContractType?: string | null) => {
    fetchContractAbort?.abort();
    fetchContractAbort = new AbortController();
    const { signal } = fetchContractAbort;

    try {
      set({ contract: null });
      const { data: contractStatusResponse } = await getApolloClient().query({
        query: VERIFY_CONTRACT_STATUS,
        fetchPolicy: 'network-only',
        context: { fetchOptions: { signal } },
      });
      const statuses = contractStatusResponse.verifyContractStatus ?? [];
      const items =
        useCoreLoopConfigurationStore.getState().coreLoopConfigurationItems;
      const multi = items?.enableMultiContractOnboarding === true;
      const selected = pickContractStatusForOnboarding(
        statuses,
        pageContractType,
        multi,
      );
      if (!selected?.contractId) {
        set({ contract: null });
        return;
      }
      const { data: contractDataResponse } = await getApolloClient().query({
        query: GET_CONTRACT,
        variables: {
          id: selected.contractId,
        },
        fetchPolicy: 'network-only',
        context: { fetchOptions: { signal } },
      });
      set({ contract: contractDataResponse.getContract });
    } catch (error: unknown) {
      if (signal.aborted) {
        return;
      }
      const name =
        error && typeof error === 'object' && 'name' in error
          ? String((error as { name: unknown }).name)
          : '';
      if (name === 'AbortError') {
        return;
      }
      throw error;
    }
  },
  fetchContractById: async (contractId: string) => {
    const { data } = await getApolloClient().query({
      query: GET_CONTRACT,
      variables: {
        id: contractId,
      },
      fetchPolicy: 'network-only',
    });

    return data.getContract;
  },
  fetchContractsByOwnerId: async (
    ownerId: string,
    tenantId: string,
    activeOnly?: boolean,
  ) => {
    const { data } = await getApolloClient().query({
      query: GET_CONTRACTS_BY_OWNER_ID,
      variables: {
        ownerId,
        tenantId,
        activeOnly,
      },
    });
    return data.getContractsByOwnerId ?? [];
  },
  agreeToContract: async (contractId: string, signature?: string) => {
    // Extract base64 string from data URI if present (format: data:image/png;base64,<base64string>)
    let base64Signature: string | undefined = signature;
    if (signature && signature.includes(',')) {
      base64Signature = signature.split(',')[1];
    }

    const { data } = await getApolloClient().mutate({
      mutation: AGREE_TO_CONTRACT,
      variables: {
        contractId,
        signature: base64Signature,
      },
    });
    set({ contract: data.agreeToContract });

    return data.agreeToContract;
  },
}));
